import React, { useEffect, useMemo, useState, lazy, Component } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, ChevronRight, ChevronDown, ExternalLink, PlayCircle, Clock, FileText, HelpCircle, ListChecks } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getContentSections, getContentSection, readContentCache, formatVideoDuration, getVideoEmbed, ContentSection, SectionType, FaqItem, GuideStep } from '../../api/content';
import { SkeletonBlock } from '../../components/SkeletonBlock';
import { Button } from '../../components/Button';
import { Markdown } from '../../components/Markdown';
const TYPE_ICON: Record<SectionType, {
  icon: ComponentType<{
    size?: number;
    className?: string;
  }>;
  tint: string;
  label: string;
}> = {
  article: {
    icon: FileText,
    tint: 'bg-primary/10 text-primary',
    label: 'Article'
  },
  faq: {
    icon: HelpCircle,
    tint: 'bg-accent/15 text-accent',
    label: 'FAQ'
  },
  video: {
    icon: PlayCircle,
    tint: 'bg-amber-500/15 text-amber-600',
    label: 'Video'
  },
  guide: {
    icon: ListChecks,
    tint: 'bg-blue-500/10 text-blue-600',
    label: 'Guide'
  }
};
export function LearnSection() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [allSections, setAllSections] = useState<ContentSection[]>(() => readContentCache() || []);
  const [section, setSection] = useState<ContentSection | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetchAll = async () => {
    setIsLoading(true);
    setError(false);
    try {
      // Always refresh the full list — gives us the section + children + cache
      const fresh = await getContentSections();
      setAllSections(fresh);
      const found = fresh.find(s => s.slug === slug) || null;
      if (found) {
        setSection(found);
      } else {
        // Fall back to direct slug lookup in case it isn't in the list endpoint
        const direct = await getContentSection(slug || '');
        setSection(direct);
      }
    } catch (err: any) {
      // If we have cached sections, use them; otherwise show error
      if (allSections.length > 0) {
        const found = allSections.find(s => s.slug === slug) || null;
        setSection(found);
      } else {
        setError(true);
      }
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  const children = useMemo(() => {
    if (!section) return [];
    return allSections.filter(s => s.parentId === section.id).sort((a, b) => a.orderIndex - b.orderIndex);
  }, [allSections, section]);
  return <main className="w-full max-w-[800px] mx-auto pb-20 md:pb-0 animate-fadeInUp" data-id="element-1319" data-mp="1b06gjgu47rvt">
      {/* Back nav */}
      <div className="flex items-center mb-6" data-id="element-1320" data-mp="1659748sxbgie">
        <button onClick={() => navigate('/app/learn')} className="w-[40px] h-[40px] rounded-full flex items-center justify-center hover:bg-border/50 transition-colors mr-3" aria-label="Go back" data-id="element-1321" data-mp="3hrb6isu7uihe">
          <ArrowLeft size={24} className="text-text-primary" data-id="element-1322" data-mp="3qpwuj3igippk" />
        </button>
        <h1 className="text-[18px] font-semibold text-text-primary" data-id="element-1323" data-mp="3viq1ly1eea4t">
          Back to Learn
        </h1>
      </div>

      {isLoading ? <div className="space-y-6" data-id="element-1324" data-mp="4dzmj34mzjzq">
          <SkeletonBlock height="220px" borderRadius="24px" data-id="element-1325" data-mp="99x6djf3z8h9" />
          <SkeletonBlock height="20px" width="60%" data-id="element-1326" data-mp="9cjcrfq9jhxw" />
          <SkeletonBlock height="20px" width="90%" data-id="element-1327" data-mp="eb4ddg9fg3mv" />
          <SkeletonBlock height="20px" width="80%" data-id="element-1328" data-mp="ibegml5u4fy1" />
        </div> : error ? <ErrorCard onRetry={fetchAll} data-id="element-1329" data-mp="i923clb0utrb" /> : section ? <SectionRenderer section={section} children={children} navigate={navigate} data-id="element-1330" data-mp="2g2hf5wg3lvep" /> : <NotFoundCard onBack={() => navigate('/app/learn')} data-id="element-1331" data-mp="2k4ku4v1z7i4q" />}
    </main>;
}
// ─── Sub-components ─────────────────────────────────────────────────────────
function SectionRenderer({
  section,
  children,
  navigate
}: {
  section: ContentSection;
  children: ContentSection[];
  navigate: (path: string) => void;
}) {
  const typeMeta = TYPE_ICON[section.sectionType] || TYPE_ICON.article;
  const TypeIcon = typeMeta.icon;
  const duration = formatVideoDuration(section.videoDurationSeconds);
  return <div className="space-y-8" data-id="element-1332" data-mp="1130hcdemvee6">
      {/* HERO (article / faq / guide use mediaUrl; video uses its own player block) */}
      {section.sectionType !== 'video' && section.mediaUrl && <div className="rounded-[24px] overflow-hidden shadow-card" data-id="element-1333" data-mp="1ftpuvmfkh5l7">
          <img src={section.mediaUrl} alt="" className="w-full h-[200px] md:h-[260px] object-cover" data-id="element-1334" data-mp="1azln50yh8tj2" />
        </div>}

      {/* TITLE */}
      <div data-id="element-1335" data-mp="ibprlzkn97nv">
        <div className="flex items-center gap-2 mb-3" data-id="element-1336" data-mp="ibzhln5ovetk">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill text-[11px] font-semibold uppercase tracking-wider ${typeMeta.tint}`} data-id="element-1337" data-mp="2tzciu1gv9eh1">
            <TypeIcon size={12} data-id="element-1338" data-mp="2kz6a0sw452o7" />
            {typeMeta.label}
          </span>
          {duration && <span className="inline-flex items-center gap-1 text-[12px] text-text-secondary" data-id="element-1339" data-mp="2p0i0f0mt9rxg">
              <Clock size={12} data-id="element-1340" data-mp="3cqc9r87x8ycy" />
              {duration}
            </span>}
        </div>
        <h1 className="text-[26px] md:text-[32px] font-bold text-text-primary leading-tight" data-id="element-1341" data-mp="3hpgnmc5sjk9m">
          {section.title}
        </h1>
      </div>

      {/* TYPE-SPECIFIC BODY */}
      {section.sectionType === 'article' && <ArticleBody content={section.content} data-id="element-1342" data-mp="38nz74soh1gke" />}
      {section.sectionType === 'faq' && <FaqBody content={section.content} faqs={section.data?.faqs || []} data-id="element-1343" data-mp="1sa8zjby58a60" />}
      {section.sectionType === 'video' && <VideoBody videoUrl={section.videoUrl} mediaUrl={section.mediaUrl} description={section.content} data-id="element-1344" data-mp="1xb6frizleu4v" />}
      {section.sectionType === 'guide' && <GuideBody content={section.content} steps={section.data?.steps || []} data-id="element-1345" data-mp="3u07vmmsul7gh" />}

      {/* CHILDREN */}
      {children.length > 0 && <div className="pt-4" data-id="element-1346" data-mp="3l6bclguzcb5a">
          <h2 className="text-[20px] font-semibold text-text-primary mb-4" data-id="element-1347" data-mp="3l74b5ujsbucc">
            More in this section
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1348" data-mp="3b96mx71xdxjq">
            {children.map(child => {
          const cmeta = TYPE_ICON[child.sectionType] || TYPE_ICON.article;
          const CIcon = cmeta.icon;
          return <button key={child.slug} onClick={() => navigate(`/app/learn/${child.slug}`)} className="bg-white rounded-[20px] p-5 shadow-sm border border-border hover:border-primary/30 hover:shadow-card transition-all text-left flex items-center justify-between group" data-id="element-1349" data-mp="329tnl77impcq">
                  <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-1350" data-mp="2icxold706dmg">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${cmeta.tint.split(' ')[0]}`} data-id="element-1351" data-mp="2nc0n0esh9llg">
                      <CIcon size={16} className={cmeta.tint.split(' ')[1]} data-id="element-1352" data-mp="2w4vjaizyw6z3" />
                    </div>
                    <div className="min-w-0" data-id="element-1353" data-mp="1zh7k9jbll55e">
                      <h3 className="text-[15px] font-semibold text-text-primary group-hover:text-primary transition-colors truncate" data-id="element-1354" data-mp="24ezggpj9zdfe">
                        {child.title}
                      </h3>
                      <p className="text-[12px] text-text-secondary uppercase tracking-wider" data-id="element-1355" data-mp="2dfnq781myrmh">
                        {cmeta.label}
                      </p>
                    </div>
                  </div>
                  <ChevronRight size={18} className="text-text-secondary group-hover:text-primary transition-colors flex-shrink-0 ml-3" data-id="element-1356" data-mp="3oukkoxgla918" />
                </button>;
        })}
          </div>
        </div>}
    </div>;
}
// ─── Article ────────────────────────────────────────────────────────────────
function ArticleBody({
  content
}: {
  content: string | null;
}) {
  if (!content) {
    return <div className="bg-secondary/40 rounded-[20px] p-6 text-center text-[14px] text-text-secondary" data-id="element-1357" data-mp="34i164hky9hg5">
        This article has no content yet.
      </div>;
  }
  return <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-card" data-id="element-1358" data-mp="3e95n1mu6rcdm">
      <Markdown data-id="element-1359" data-mp="3s9q0s4l8ui09">{content}</Markdown>
    </div>;
}
// ─── FAQ ────────────────────────────────────────────────────────────────────
function FaqBody({
  content,
  faqs
}: {
  content: string | null;
  faqs: FaqItem[];
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  return <div className="space-y-6" data-id="element-1360" data-mp="1tulmuzfh0300">
      {content && <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-card" data-id="element-1361" data-mp="1tw6mh3gn6c4q">
          <Markdown data-id="element-1362" data-mp="1jqous3ebo4i5">{content}</Markdown>
        </div>}

      {faqs.length === 0 ? <div className="bg-secondary/40 rounded-[20px] p-6 text-center text-[14px] text-text-secondary" data-id="element-1363" data-mp="1nqyzzduaiqvy">
          No questions have been added yet.
        </div> : <div className="bg-white rounded-[24px] shadow-card overflow-hidden divide-y divide-border" data-id="element-1364" data-mp="1eydmac5inadz">
          {faqs.map((item, i) => {
        const isOpen = openIndex === i;
        return <div key={i} data-id="element-1365" data-mp="15222l13lji9i" data-mpi={i}>
                <button onClick={() => setOpenIndex(isOpen ? null : i)} className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-secondary/30 transition-colors" aria-expanded={isOpen} data-id="element-1366" data-mp="152ak56anzbr1">
                  <span className="text-[15px] font-semibold text-text-primary pr-4" data-id="element-1367" data-mp="2lgk5rsniume6">
                    {item.q}
                  </span>
                  <ChevronDown size={20} className={`text-text-secondary flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-1368" data-mp="2qbzfc8ehxw4s" />
                </button>
                <AnimatePresence initial={false} data-id="element-1369" data-mp="2ebdbp9gmrotj">
                  {isOpen && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.22,
              ease: 'easeOut'
            }} className="overflow-hidden" data-id="element-1370" data-mp="2icf4b7g1l2mj">
                      <div className="px-6 pb-5 pt-1" data-id="element-1371" data-mp="1qmair5gjq9jq">
                        <Markdown data-id="element-1372" data-mp="1i48me2t4mlhx">{item.a}</Markdown>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
      })}
        </div>}
    </div>;
}
// ─── Video ──────────────────────────────────────────────────────────────────
function VideoBody({
  videoUrl,
  mediaUrl,
  description
}: {
  videoUrl: string | null;
  mediaUrl: string | null;
  description: string | null;
}) {
  if (!videoUrl) {
    return <div className="bg-danger/5 border border-danger/15 rounded-[20px] p-6 flex items-center gap-4" data-id="element-1373" data-mp="uext16e3vtdt">
        <AlertTriangle size={24} className="text-danger flex-shrink-0" data-id="element-1374" data-mp="193ah7h6yc7xr" />
        <div data-id="element-1375" data-mp="195n3g6jqt4gm">
          <p className="text-[15px] font-semibold text-text-primary mb-1" data-id="element-1376" data-mp="14cu45y71mobx">
            Video unavailable
          </p>
          <p className="text-[13px] text-text-secondary" data-id="element-1377" data-mp="gmpx1gunc6g6">
            This video doesn't have a playable URL yet.
          </p>
        </div>
      </div>;
  }
  const embed = getVideoEmbed(videoUrl);
  return <div className="space-y-6" data-id="element-1378" data-mp="2br7hui8bn0ym">
      {embed && (embed.provider === 'youtube' || embed.provider === 'vimeo') ? <div className="rounded-[24px] overflow-hidden shadow-card bg-black" data-id="element-1379" data-mp="2go7nwt5683pq">
          <div className="relative w-full" style={{
        aspectRatio: '16 / 9'
      }} data-id="element-1380" data-mp="2lfz7gjpwt96d">
            <iframe src={embed.embedUrl} title="Video player" className="absolute inset-0 w-full h-full" frameBorder={0} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen data-id="element-1381" data-mp="2ph154w46hd68" />
          </div>
        </div> :
    // External / unknown provider — show clickable thumbnail card
    <a href={videoUrl} target="_blank" rel="noopener noreferrer" className="block rounded-[24px] overflow-hidden shadow-card bg-white border border-border hover:border-primary/30 hover:shadow-elevated transition-all" data-id="element-1382" data-mp="3d86m5eluubi4">
          {mediaUrl ? <div className="relative h-[220px] bg-secondary overflow-hidden" data-id="element-1383" data-mp="3nb26af1aa3mh">
              <img src={mediaUrl} alt="" className="w-full h-full object-cover" data-id="element-1384" data-mp="3ikdfl1xfhaoe" />
              <div className="absolute inset-0 flex items-center justify-center bg-black/30" data-id="element-1385" data-mp="3ei8pgd66uw6i">
                <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-lg" data-id="element-1386" data-mp="39kqd0csypytr">
                  <PlayCircle size={32} className="text-primary" data-id="element-1387" data-mp="34j9eup1v4qgv" />
                </div>
              </div>
            </div> : <div className="h-[180px] bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center" data-id="element-1388" data-mp="2vk5vdjysa91u">
              <PlayCircle size={48} className="text-primary" data-id="element-1389" data-mp="xuwcgdh42o5b" />
            </div>}
          <div className="p-5 flex items-center justify-between" data-id="element-1390" data-mp="ts9j284skv60">
            <span className="text-[14px] font-medium text-text-primary" data-id="element-1391" data-mp="tqor8q9v3qbu">
              Open video in new tab
            </span>
            <ExternalLink size={18} className="text-text-secondary" data-id="element-1392" data-mp="15uu11c7u1iax" />
          </div>
        </a>}

      {description && <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-card" data-id="element-1393" data-mp="ru9kyntay0l4">
          <Markdown data-id="element-1394" data-mp="vwd6k59oqlk4">{description}</Markdown>
        </div>}
    </div>;
}
// ─── Guide ──────────────────────────────────────────────────────────────────
function GuideBody({
  content,
  steps
}: {
  content: string | null;
  steps: GuideStep[];
}) {
  return <div className="space-y-6" data-id="element-1395" data-mp="my18ji6holhn">
      {content && <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-card" data-id="element-1396" data-mp="88d5ocintert">
          <Markdown data-id="element-1397" data-mp="d5vgy9k5g62z">{content}</Markdown>
        </div>}

      {steps.length === 0 ? <div className="bg-secondary/40 rounded-[20px] p-6 text-center text-[14px] text-text-secondary" data-id="element-1398" data-mp="3vc9wgcy2go3h">
          No steps have been added to this guide yet.
        </div> : <ol className="space-y-4" data-id="element-1399" data-mp="3gpb5sk719xpt">
          {steps.map((step, i) => <li key={i} className="bg-white rounded-[24px] p-6 md:p-7 shadow-card flex gap-4 md:gap-5" data-id="element-1400" data-mp="3gpkw68vzq6w1" data-mpi={i}>
              <div className="flex flex-col items-center flex-shrink-0" data-id="element-1401" data-mp="3pqis03w9z83j">
                <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center text-[15px] font-bold shadow-sm" data-id="element-1402" data-mp="3pno9ihwtnxcr">
                  {i + 1}
                </div>
                {i < steps.length - 1 && <div className="w-px flex-1 bg-border mt-2 min-h-[20px]" data-id="element-1403" data-mp="3upvnfd17pnhq" />}
              </div>
              <div className="flex-1 min-w-0" data-id="element-1404" data-mp="d7at4rqj945v">
                <h3 className="text-[17px] font-semibold text-text-primary mb-2 leading-snug" data-id="element-1405" data-mp="89j1zzxppm2q">
                  {step.heading}
                </h3>
                {step.body && <Markdown data-id="element-1406" data-mp="m9tsh1v40ynd">{step.body}</Markdown>}
                {step.image && <div className="mt-4 rounded-[16px] overflow-hidden border border-border" data-id="element-1407" data-mp="hb8w5dizaz47">
                    <img src={step.image} alt="" className="w-full h-auto object-cover" loading="lazy" data-id="element-1408" data-mp="3b38fhe2b0jdc" />
                  </div>}
              </div>
            </li>)}
        </ol>}
    </div>;
}
// ─── Error / Not-found ──────────────────────────────────────────────────────
function ErrorCard({
  onRetry
}: {
  onRetry: () => void;
}) {
  return <div className="bg-danger/10 border border-danger/20 rounded-[20px] p-6 text-center" data-id="element-1409" data-mp="2lue61bgn3bm">
      <AlertTriangle size={32} className="text-danger mx-auto mb-3" data-id="element-1410" data-mp="7kxc71t0w9a3" />
      <h3 className="text-[16px] font-semibold text-text-primary mb-2" data-id="element-1411" data-mp="7jm9747ej5k1">
        Failed to load content
      </h3>
      <p className="text-[14px] text-text-secondary mb-4" data-id="element-1412" data-mp="29ctoidn0ln5x">
        There was a problem connecting to the server.
      </p>
      <Button onClick={onRetry} className="mx-auto" data-id="element-1413" data-mp="2ddna30czipvn">
        Try Again
      </Button>
    </div>;
}
function NotFoundCard({
  onBack
}: {
  onBack: () => void;
}) {
  return <div className="flex flex-col items-center justify-center text-center py-16 px-4" data-id="element-1414" data-mp="214623kgccky2">
      <div className="w-[120px] h-[120px] rounded-full bg-primary/10 flex items-center justify-center mb-6" data-id="element-1415" data-mp="212csbwaflklu">
        <FileText size={48} className="text-primary" data-id="element-1416" data-mp="1s3hnanj4igk4" />
      </div>
      <h3 className="text-[20px] font-semibold text-text-primary mb-2" data-id="element-1417" data-mp="1s5c7u0qhg6no">
        Content coming soon
      </h3>
      <p className="text-[15px] text-text-secondary max-w-[300px] mb-8" data-id="element-1418" data-mp="2ornvtlj61t3o">
        Check back later.
      </p>
      <Button onClick={onBack} variant="secondary" data-id="element-1419" data-mp="2jsuibt3vq23i">
        Back to Learn
      </Button>
    </div>;
}